<template>
  <component
    :is="tag"
    :type="tag === 'button' ? type : undefined"
    :class="buttonClasses"
    :disabled="disabled || loading"
    v-bind="$attrs"
  >
    <span v-if="loading" class="spinner"></span>
    <slot v-if="!loading"></slot>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  tag?: 'button' | 'a';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  tag: 'button',
  type: 'button',
  disabled: false,
  loading: false,
  fullWidth: false,
});

const buttonClasses = computed(() => {
  const baseClasses = [
    'btn',
    'inline-flex',
    'items-center',
    'justify-center',
    'font-semibold',
    'rounded-lg',
    'transition-all',
    'duration-normal',
    'ease-smooth',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
  ];

  // Variants
  const variantClasses = {
    primary: [
      'bg-primary-600',
      'text-white',
      'hover:bg-primary-700',
      'hover:shadow-primary',
      'focus-visible:ring-primary-500',
      'active:bg-primary-800',
    ],
    secondary: [
      'bg-secondary-500',
      'text-white',
      'hover:bg-secondary-600',
      'hover:shadow-secondary',
      'focus-visible:ring-secondary-500',
      'active:bg-secondary-700',
    ],
    accent: [
      'bg-accent-600',
      'text-white',
      'hover:bg-accent-700',
      'hover:shadow-accent',
      'focus-visible:ring-accent-500',
      'active:bg-accent-800',
    ],
    outline: [
      'bg-transparent',
      'border-2',
      'border-primary-600',
      'text-primary-600',
      'hover:bg-primary-50',
      'hover:border-primary-700',
      'focus-visible:ring-primary-500',
      'active:bg-primary-100',
    ],
    ghost: [
      'bg-transparent',
      'text-primary-600',
      'hover:bg-primary-50',
      'focus-visible:ring-primary-500',
      'active:bg-primary-100',
    ],
    danger: [
      'bg-red-600',
      'text-white',
      'hover:bg-red-700',
      'hover:shadow-lg',
      'focus-visible:ring-red-500',
      'active:bg-red-800',
    ],
  };

  // Sizes
  const sizeClasses = {
    sm: ['text-sm', 'px-3', 'py-1.5', 'gap-1.5'],
    md: ['text-base', 'px-4', 'py-2', 'gap-2'],
    lg: ['text-lg', 'px-6', 'py-3', 'gap-2'],
    xl: ['text-xl', 'px-8', 'py-4', 'gap-3'],
  };

  const classes = [
    ...baseClasses,
    ...variantClasses[props.variant],
    ...sizeClasses[props.size],
  ];

  if (props.fullWidth) {
    classes.push('w-full');
  }

  return classes.join(' ');
});
</script>

<style scoped>
.btn {
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn:active::before {
  width: 300px;
  height: 300px;
}

.spinner {
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

